import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../utils/dateUtils';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

interface CalendarView {
  view: 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';
  label: string;
}

interface Booking {
  id: string;
  booking_date: string;
  start_time: string;
  end_time: string;
  first_name: string;
  last_name: string;
  group_type: 'group' | 'reduced' | 'private';
  persons: number;
  notes?: string;
  color?: string;
  tour: {
    name: string;
  };
}

const BookingCalendar = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const { data, error } = await supabase
          .from('bookings')
          .select(`
            id,
            booking_date,
            start_time,
            end_time,
            first_name,
            last_name,
            group_type,
            persons,
            notes,
            color,
            status,
            tour:tours (
              name
            )
          `)
          .neq('status', 'cancelled')
          .order('booking_date', { ascending: true });

        if (error) throw error;

        setBookings(data || []);
      } catch (err) {
        console.error('Error fetching bookings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', checkMobile);
    checkMobile();

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getEventColor = (groupType: string) => {
    switch (groupType) {
      case 'private':
        return '#953B0E';
      case 'reduced':
        return '#2A382A';
      default:
        return '#E85C0D';
    }
  };

  const calendarEvents = bookings.map(booking => ({
    id: booking.id,
    title: `${booking.tour.name} - ${booking.first_name} ${booking.last_name}`,
    start: `${booking.booking_date}T${booking.start_time}`,
    end: `${booking.booking_date}T${booking.end_time}`,
    backgroundColor: booking.color || getEventColor(booking.group_type),
    extendedProps: {
      persons: booking.persons,
      groupType: booking.group_type,
      notes: booking.notes
    }
  }));

  return (
    <div className="space-y-6 w-full">
      <div className="bg-white/0 rounded-xl p-2 sm:p-4 md:p-6 overflow-x-auto">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: isMobile ? 'prev,next' : 'prev,next today',
            center: isMobile ? '' : 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          events={calendarEvents}
          eventClick={({ event }) => {
            const booking = bookings.find(b => b.id === event.id);
            if (booking) {
              setSelectedBooking(booking);
              setShowModal(true);
            }
          }}
          height="auto"
          aspectRatio={isMobile ? 0.8 : 1.8}
          expandRows={true}
          stickyHeaderDates={true}
          nowIndicator={true}
          businessHours={{
            daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
            startTime: '06:00',
            endTime: '22:00',
          }}
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          views={{
            timeGridWeek: {
              titleFormat: { year: 'numeric', month: 'short', day: 'numeric' },
              dayHeaderFormat: { weekday: isMobile ? 'narrow' : 'short' },
              slotLabelFormat: { hour: 'numeric', minute: '2-digit', hour12: true }
            }
          }}
        />
      </div>

      {loading && (
        <div className="text-center text-white/50">Loading bookings...</div>
      )}

      {/* Booking Details Modal */}
      {showModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full">
            <h3 className="text-xl font-semibold text-[#2A382A] mb-4">
              Booking Details
            </h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-[#2A382A]">Tour</h4>
                <p className="text-[#2A382A]/70">{selectedBooking.tour.name}</p>
              </div>
              <div>
                <h4 className="font-medium text-[#2A382A]">Customer</h4>
                <p className="text-[#2A382A]/70">
                  {selectedBooking.first_name} {selectedBooking.last_name}
                </p>
              </div>
              <div>
                <h4 className="font-medium text-[#2A382A]">Group</h4>
                <p className="text-[#2A382A]/70">
                  {selectedBooking.group_type} tour - {selectedBooking.persons} persons
                </p>
              </div>
              {selectedBooking.notes && (
                <div>
                  <h4 className="font-medium text-[#2A382A]">Notes</h4>
                  <p className="text-[#2A382A]/70">{selectedBooking.notes}</p>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-[#2A382A] text-white rounded-lg hover:bg-[#1F2B1F] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCalendar;